import { getSportSetting } from '@/services/settings.service';
import type { BetTypeFilterCriterion } from '@/types/settings';

const PIPE_SEPARATOR = /\|/;

const parseCommaSeparatedNumbers = (value: string): number[] =>
	value
		.split(',')
		.map(segment => segment.trim())
		.filter(segment => segment.length > 0)
		.map(segment => Number(segment))
		.filter(num => !Number.isNaN(num));

/**
 * Parses config like "1:2,3,4 | 5:6,7,8" into sport ID -> ordered bet type IDs.
 */
export function parseOddsPageFilterBetTypesConfig(
	config?: string | null,
): Record<number, number[]> {
	if (!config?.trim()) {
		return {};
	}

	const result: Record<number, number[]> = {};

	for (const segment of config.split(PIPE_SEPARATOR)) {
		const trimmedSegment = segment.trim();
		if (!trimmedSegment) {
			continue;
		}

		const colonIndex = trimmedSegment.indexOf(':');
		if (colonIndex <= 0 || colonIndex >= trimmedSegment.length - 1) {
			continue;
		}

		const sportId = Number(trimmedSegment.slice(0, colonIndex).trim());
		if (Number.isNaN(sportId)) {
			continue;
		}

		const betTypes = parseCommaSeparatedNumbers(trimmedSegment.slice(colonIndex + 1));
		if (betTypes.length === 0) {
			continue;
		}

		result[sportId] = betTypes;
	}

	return result;
}

const getDefaultOddsPageFilterBetTypes = (sportId: number): BetTypeFilterCriterion[] =>
	getSportSetting(sportId).oddsPageFilterBetTypes ?? getSportSetting(0).oddsPageFilterBetTypes;

const matchesLicBetType = (criterion: BetTypeFilterCriterion, licBetTypeId: number): boolean =>
	criterion.betTypes.includes(licBetTypeId);

/**
 * Merges LIC bet-type order with static defaults:
 * - LIC-listed bet types appear first (in config order), reusing static criterion when matched
 * - Remaining default items follow in their original order
 */
export function resolveOddsPageFilterBetTypes(
	sportId: number,
	licOverrides?: Record<number, number[]>,
): BetTypeFilterCriterion[] {
	const defaultItems = getDefaultOddsPageFilterBetTypes(sportId);
	const licBetTypes = licOverrides?.[sportId];

	if (!licBetTypes?.length) {
		return defaultItems;
	}

	const usedIndices = new Set<number>();
	const result: BetTypeFilterCriterion[] = [];

	for (const licBetTypeId of licBetTypes) {
		const matchIndex = defaultItems.findIndex(
			(item, index) => !usedIndices.has(index) && matchesLicBetType(item, licBetTypeId),
		);

		if (matchIndex >= 0) {
			usedIndices.add(matchIndex);
			result.push(defaultItems[matchIndex]);
		} else {
			result.push({ betTypes: [licBetTypeId] });
		}
	}

	defaultItems.forEach((item, index) => {
		if (!usedIndices.has(index)) {
			result.push(item);
		}
	});

	return result;
}

export function hasLicOddsPageFilterBetTypesOverride(
	sportId: number,
	licOverrides?: Record<number, number[]>,
): boolean {
	return Boolean(licOverrides?.[sportId]?.length);
}
